import { Attachment, canEditAttachment, Organization, User } from '@ee/common/models';
import { Permission } from '@ee/common/enums';

/**
 * Whether this user may fill in a PDF's AcroForm fields and save the result.
 *
 * Deliberately NOT the same check as `canActivateEFilingCenter`. That guard also requires an active
 * EFILING dependent subscription, which costs an HTTP round-trip — a dialog cannot wait on one without
 * either flickering its buttons or firing a billing request on every document preview in the app. This
 * pair is readable synchronously from the ngrx store, so the dialog's actions are right on first paint.
 *
 * `EFILING_SUBMIT` rather than `EFILING_VIEW`: filling a court form is writing to a document that gets
 * filed, so a read-only e-filing viewer must not be handed an editable one.
 *
 * It ALSO requires document edit on this attachment's kind. Saving a filled form changes an existing
 * document, so the server gates it on `case:{upload,generate}-documents:edit` exactly as it gates a
 * rename. Without this check an e-filer lacking that permission got editable fields and a SAVE that
 * 403s — after the 2-90s upload scan, with their typing lost.
 *
 * This is a UX gate. The enforcement is the endpoint's own `@PreAuthorize` plus its org
 * premium-feature and per-kind edit checks — a client that lies gets a 403, not a saved document.
 */
export function canFillPdfForms(
  org?: Organization | null,
  user?: User | null,
  attachment?: Attachment | null
): boolean {
  return !!org?.premium_features?.['EFILING']
    && !!user?.permissions?.includes(Permission.EFILING_SUBMIT)
    && canEditAttachment(user?.permissions, attachment);
}
